import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';

import ErrorBoundary from './components/common/ErrorBoundary';
import { Spinner } from './components/common/PageHeader';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { queryClient } from './lib/queryClient';
import BillingConfigPage from './pages/BillingConfigPage';
import BillingPage from './pages/BillingPage';
import CapitalPage from './pages/CapitalPage';
import ClientsPage from './pages/ClientsPage';
import Dashboard from './pages/Dashboard';
import EmployeesPage from './pages/EmployeesPage';
import ExpensesPage from './pages/ExpensesPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import Login from './pages/Login';
import PayrollPage from './pages/PayrollPage';
import RecurringExpensesPage from './pages/RecurringExpensesPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';

function ProtectedRoute({ children, permissions }: { children: React.ReactNode; permissions?: string[] }) {
  const { isAuthenticated, isLoading, hasAnyPermission } = useAuth();

  if (isLoading) return <Spinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (permissions?.length && !hasAnyPermission(permissions)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<ProtectedRoute permissions={['dashboard:view']}><Dashboard /></ProtectedRoute>} />
        <Route path="/clients" element={<ProtectedRoute permissions={['clients:view']}><ClientsPage /></ProtectedRoute>} />
        <Route path="/billing-config" element={<ProtectedRoute permissions={['billing-config:view']}><BillingConfigPage /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute permissions={['billing:view']}><BillingPage /></ProtectedRoute>} />
        <Route path="/billing/invoices/:id" element={<ProtectedRoute permissions={['billing:view']}><InvoiceDetailPage /></ProtectedRoute>} />
        <Route path="/expenses" element={<ProtectedRoute permissions={['expenses:view']}><ExpensesPage /></ProtectedRoute>} />
        <Route path="/recurring-expenses" element={<ProtectedRoute permissions={['expenses:view']}><RecurringExpensesPage /></ProtectedRoute>} />
        <Route path="/employees" element={<ProtectedRoute permissions={['employees:view']}><EmployeesPage /></ProtectedRoute>} />
        <Route path="/payroll" element={<ProtectedRoute permissions={['employees:view']}><PayrollPage /></ProtectedRoute>} />
        <Route path="/capital" element={<ProtectedRoute permissions={['capital:view']}><CapitalPage /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute permissions={['reports:view']}><ReportsPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
